import { IPatientRepository } from "../../adapters/driven/repositories/patientRepository";

export const PatientServiceImplFactory = ({
  patientRepository,
}: {
  patientRepository: IPatientRepository;
}) => {
  const normalizeDeviceId = (deviceId: string) =>
    deviceId.replace(/[-\s]/g, "").toLowerCase();

  const isPatientDeviceAPIError = (
    response: unknown
  ): response is IPatientDeviceAPIError =>
    typeof response === "object" &&
    response != null &&
    "code" in response &&
    "message" in response;

  const getPatientDeviceAPIError = (
    response: unknown,
    fallback: string
  ): { code?: string; message: string } => {
    if (isPatientDeviceAPIError(response)) return response;
    if (
      typeof response === "object" &&
      response != null &&
      "detail" in response &&
      typeof response.detail === "object" &&
      response.detail != null &&
      "message" in response.detail &&
      typeof response.detail.message === "string"
    ) {
      return { message: response.detail.message };
    }
    return { message: fallback };
  };

  const getPatientList: IGetPatientList = async ({
    clinicId,
    floorIds,
    shouldCall = true,
  }) => {
    try {
      if (shouldCall) {
        const response = await patientRepository.getPatientListAPI({
          clinic_id: clinicId,
          floor_ids: floorIds,
          consent_status: [1, 2],
        });
        return {
          success: true,
          data: response,
        };
      }
      return {
        success: false,
        error: { message: "" },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientSummary: IGetPatientSummary = async ({
    clinicId,
    patientId,
  }) => {
    try {
      const response = await patientRepository.getPatientSummaryAPI({
        clinic_id: clinicId,
        patient_id: patientId,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const mapVnsCohort = (
    data: IVnsPatientAdmissionCohortAPIData
  ): IVnsPatientAdmissionCohort => ({
    admissionId: data.admission_id,
    patientId: data.patient_id,
    clinicId: data.clinic_id,
    cohort: data.cohort ?? null,
    updatedByUserId: data.updated_by_user_id ?? null,
    updatedAt: data.updated_at ?? null,
  });

  const getVnsPatientAdmissionCohort: IGetVnsPatientAdmissionCohort = async ({
    clinicId,
    patientId,
    admissionId,
  }) => {
    try {
      const response = await patientRepository.getVnsPatientAdmissionCohortAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        admission_id: admissionId,
      });
      return { success: true, data: mapVnsCohort(response.data) };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const updateVnsPatientAdmissionCohort: IUpdateVnsPatientAdmissionCohort =
    async ({ clinicId, patientId, admissionId, cohort }) => {
      try {
        const trimmed = cohort.trim();
        const response =
          await patientRepository.updateVnsPatientAdmissionCohortAPI({
            clinic_id: clinicId,
            patient_id: patientId,
            admission_id: admissionId,
            cohort: trimmed.length ? trimmed : null,
          });
        return { success: true, data: mapVnsCohort(response.data) };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as unknown as Error).message },
        };
      }
    };

  const getPatientCircadiaVital: IGetPatientCircadiaVital = async ({
    clinicId,
    patientId,
  }) => {
    try {
      const response = await patientRepository.getPatientCircadiaVitalAPI({
        clinic_id: clinicId,
        patient_id: patientId,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientPCCVital: IGetPatientPCCVital = async ({
    clinicId,
    patientId,
  }) => {
    try {
      const response = await patientRepository.getPatientPCCVitalAPI({
        clinic_id: clinicId,
        patient_id: patientId,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientEventList: IGetPatientEventList = async ({
    clinicId,
    patientId,
    eventType,
    eventDate,
    page,
    page_size,
    shouldCall = true,
  }) => {
    if (!shouldCall) {
      return {
        success: false,
        error: { message: "" },
      };
    }
    const payload: Parameters<
      typeof patientRepository.getPatientEventListAPI
    >[0] = {
      clinic_id: clinicId,
      resident_id: patientId,
      event_type: eventType,
      page,
      page_size,
    };
    if (eventDate) {
      payload.event_date = eventDate;
    }

    try {
      const response = await patientRepository.getPatientEventListAPI(payload);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientVitalGraph: IGetPatientVitalGraphService = async ({
    clinicId,
    endDate,
    patientId,
    typeOfRange,
    hourly_format = false,
  }) => {
    try {
      const response = await patientRepository.getPatientVitalGraphAPI({
        clinic_id: clinicId,
        resident_id: patientId,
        end_date: endDate,
        time_window: typeOfRange,
        hourly_format,
      });

      return {
        success: true,
        data: { data: response },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientRRGraph: IGetPatientRRGraphService = async ({
    clinicId,
    endDate,
    patientId,
    typeOfRange,
  }) => {
    try {
      const response = await patientRepository.getPatientRRGraphAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        end_date: endDate,
        type_of_range: typeOfRange,
      });

      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientNotes: IGetPatientNotesService = async (payload) => {
    try {
      const response = await patientRepository.getPatientNotesAPI(payload);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientNotificationSettings: IGetPatientNotificationSettingsService =
    async (payload) => {
      try {
        const response =
          await patientRepository.getPatientNotificationSettingsAPI(payload);
        return {
          success: true,
          data: response,
        };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as unknown as Error).message },
        };
      }
    };

  const getPatientListV2: IGetPatientListV2Service = async ({
    clinicId,
    patientId,
  }) => {
    try {
      const response = await patientRepository.getPatientListV2API({
        clinic_id: clinicId,
        patient_id: patientId,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getDiagnoses: IGetDiagnosesService = async (payload) => {
    try {
      const response = await patientRepository.getDiagnosesAPI(payload);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const saveDiagnosis: ISaveDiagnosisService = async ({
    clinicId,
    patientId,
    diagnoses,
  }) => {
    try {
      const response = await patientRepository.saveDiagnosisAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        diagnoses,
      });
      return { success: true, data: response };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPCCNotes: IGetPCCNotesService = async (payload) => {
    try {
      const response = await patientRepository.getPCCNotesAPI(payload);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPCCNotesByMonth: IGetPCCNotesByMonthService = async (payload) => {
    try {
      const response = await patientRepository.getPCCNotesByMonthAPI(payload);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientHmac: IGetPatientHmacService = async (payload) => {
    try {
      const response = await patientRepository.getPatientHmacAPI(payload);
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const addPatientNote: IAddPatientNoteService = async ({
    clinicId,
    patientId,
    file,
    notes,
  }) => {
    try {
      const response = await patientRepository.addPatientNoteAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        file,
        notes,
        is_billable: false,
        type: "CircadiaNotes",
      });

      return {
        success: true,
        data: { data: response },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getCircadiaIQ: IGetCircadiaIQ = async (payload) => {
    try {
      const response = await patientRepository.getCircadiaIQAPI(payload);
      return {
        success: true,
        data: { data: response },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getOrderIQ: IGetOrderIQ = async (payload) => {
    try {
      const response = await patientRepository.getOrderIQAPI(payload);
      return { success: true, data: { data: response } };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getCircadiaIQForRecertification: IGetCircadiaIQForRecertification =
    async (payload) => {
      try {
        const response =
          await patientRepository.getCircadiaIQForRecertificationAPI(payload);
        return {
          success: true,
          data: { data: response },
        };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as unknown as Error).message },
        };
      }
    };

  const generateCircadiaIQ: IGetGenerateCircadiaIQ = async (payload) => {
    try {
      const response = await patientRepository.generateCircadiaIQAPI({
        patient_id: payload.patient_id,
        clinic_id: payload.clinic_id,
        type: payload.type,
        date: payload.date,
      });
      return {
        success: true,
        data: { data: response },
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const getPatientListDashboard: IGetPatientListDashboard = async (payload) => {
    try {
      const response = await patientRepository.getPatientListDashboardAPI({
        clinic_id: payload.clinic_id,
        floorIds: payload.floorIds,
        no_of_days: payload.no_of_days,
      });
      return {
        success: true,
        data: response,
      };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as unknown as Error).message },
      };
    }
  };

  const registerPatientDevice: IRegisterPatientDevice = async ({
    clinicId,
    deviceId,
  }) => {
    try {
      const response = await patientRepository.registerPatientDeviceAPI({
        clinic_id: clinicId,
        device_id: normalizeDeviceId(deviceId),
      });
      if (isPatientDeviceAPIError(response)) {
        if (response.code === "DEVICE_ALREADY_EXISTS") {
          return { success: true, data: { alreadyExisted: true } };
        }
        return { success: false, error: response };
      }
      if (!("data" in response)) {
        return {
          success: false,
          error: getPatientDeviceAPIError(
            response,
            "Unable to register the device."
          ),
        };
      }
      return { success: true, data: { alreadyExisted: false } };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as Error).message },
      };
    }
  };

  const assignPatientDevice: IAssignPatientDevice = async ({
    clinicId,
    patientId,
    deviceId,
    name,
    deviceType,
  }) => {
    try {
      const response = await patientRepository.assignPatientDeviceAPI({
        clinic_id: clinicId,
        patient_id: patientId,
        device_id: normalizeDeviceId(deviceId),
        name: name?.trim() || undefined,
        device_type: deviceType?.trim() || undefined,
        status: 1,
      });
      if (isPatientDeviceAPIError(response)) {
        return { success: false, error: response };
      }
      if (!("data" in response)) {
        return {
          success: false,
          error: getPatientDeviceAPIError(
            response,
            "Unable to assign the device."
          ),
        };
      }
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as Error).message },
      };
    }
  };

  const removePatientDevice: IRemovePatientDevice = async ({
    clinicId,
    deviceId,
  }) => {
    try {
      const response = await patientRepository.removePatientDeviceAPI({
        clinic_id: clinicId,
        device_id: normalizeDeviceId(deviceId),
      });
      if (isPatientDeviceAPIError(response)) {
        return { success: false, error: response };
      }
      return { success: true, data: null };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPatientAssignedDevice: IGetPatientAssignedDevice = async ({
    clinicId,
    patientId,
  }) => {
    try {
      const response = await patientRepository.getPatientAssignedDeviceAPI({
        clinic_id: clinicId,
        patient_id: patientId,
      });
      if (isPatientDeviceAPIError(response)) {
        return { success: false, error: response };
      }
      if (!("data" in response)) {
        return {
          success: false,
          error: getPatientDeviceAPIError(
            response,
            "Unable to load the assigned device."
          ),
        };
      }
      return { success: true, data: response.data };
    } catch (error) {
      return {
        success: false,
        error: { message: (error as Error).message },
      };
    }
  };

  const getPatientDeviceConnectionStatus: IGetPatientDeviceConnectionStatus =
    async ({ deviceId }) => {
      try {
        const response =
          await patientRepository.getPatientDeviceConnectionStatusAPI({
            device_id: normalizeDeviceId(deviceId),
          });
        if (isPatientDeviceAPIError(response)) {
          return { success: false, error: response };
        }
        if (!("is_online" in response)) {
          return {
            success: false,
            error: getPatientDeviceAPIError(
              response,
              "Unable to check the device connection."
            ),
          };
        }
        return {
          success: true,
          data: {
            deviceId: response.device_id,
            isOnline: response.is_online,
            updatedAt: response.updated_at,
          },
        };
      } catch (error) {
        return {
          success: false,
          error: { message: (error as Error).message },
        };
      }
    };

  return {
    getPatientList,
    getPatientEventList,
    getPatientSummary,
    getPatientCircadiaVital,
    getPatientPCCVital,
    getPatientVitalGraph,
    getPatientRRGraph,
    getPatientNotes,
    getPatientNotificationSettings,
    getDiagnoses,
    getPatientListV2,
    saveDiagnosis,
    getPCCNotes,
    getPCCNotesByMonth,
    addPatientNote,
    getPatientHmac,
    getCircadiaIQ,
    getOrderIQ,
    getCircadiaIQForRecertification,
    generateCircadiaIQ,
    getPatientListDashboard,
    registerPatientDevice,
    assignPatientDevice,
    removePatientDevice,
    getPatientAssignedDevice,
    getPatientDeviceConnectionStatus,
    getVnsPatientAdmissionCohort,
    updateVnsPatientAdmissionCohort,
  };
};
